/**
 * hpGrade.js — THE single source of truth for HP colour grading.
 *
 * [UI-HP-BAR-UNIFICATION — 2026-07-25]: Before this file there were FIVE
 *   independent HP colour implementations and three different answers:
 *     1. CombatStatusStrip.hpGradeClasses  emerald/amber/red chip  (graded)
 *     2. VitalStatsCard.getHPColor/getHPBarColor  green/yellow/red (graded, +≤0 case)
 *     3. PartyPanel.getHPColor/getHPBarColor      green/yellow/red (graded, no ≤0 case)
 *     4. CombatTab Token bar                      flat bg-red-600  (UNGRADED)
 *     5. MonsterSheet bar                         flat bg-red-700  (UNGRADED)
 *   A player at 90% HP saw a full RED bar on the combat map and a full GREEN
 *   bar on the character sheet, and two different greens across surfaces.
 *
 * [DQ-4 — Jordie's ruling 2026-07-09]: "graded green→yellow→red EVERYWHERE
 *   (one universal scheme on every HP surface)". Thresholds and palette family
 *   are lifted from CombatStatusStrip — the first shipped consumer of that
 *   ruling (2026-07-11) — so the shipped answer wins and the four older
 *   surfaces converge onto it.
 *
 * [WHY A UTIL, NOT A COMPONENT]: the five surfaces render structurally
 *   different things (a text chip, a glowing rounded bar with an animated
 *   shine, a thin party bar, a 44px token strip, a monster sheet rule). What
 *   they must share is the COLOUR CONTRACT, not the markup. Sharing the
 *   contract is what the ruling asked for; forcing one JSX shape onto all five
 *   would be a bigger, riskier change that the ruling did not ask for.
 *
 * [ENDGAME — multi-system data-block engine]: `current / max` is a depleting
 *   resource track, not a D&D concept. Daggerheart HP, CoC hit points, and any
 *   Rule-System-Editor resource pool are the same shape, so this grader is
 *   system-agnostic by construction. It takes two numbers and knows nothing
 *   about 5e. Any future track (stress, sanity, ammo) can adopt it unchanged.
 *
 * [RULE 36 — no fabrication]: an ungradeable input (non-finite, or a
 *   non-positive denominator) resolves to the ALARM end of the ladder, never
 *   the healthy end. A colour bar must not invent health it could not read.
 *   Every shipped call site already guards its inputs (audited 2026-07-25:
 *   strip `?? 0`; VitalStatsCard forces max ≥ 1; PartyPanel `|| 0` / `|| 1`;
 *   CombatTab `maxHp > 0 ?`; MonsterSheet `|| 1`) — so this is a floor that
 *   holds the invariant, not a branch that fires in production.
 *
 * [A11Y]: colour is never the SOLE encoding on any consumer — every surface
 *   renders the numeric current/max next to (or in the title of) its bar.
 *   Each consumer also stamps `data-hp-grade` so the grade is machine-readable
 *   without parsing Tailwind class strings (used by the contract tests, and
 *   available to the sim harness as a Class-1 read).
 */

export const HP_GRADE_HEALTHY = 'healthy';
export const HP_GRADE_WOUNDED = 'wounded';
export const HP_GRADE_CRITICAL = 'critical';
export const HP_GRADE_DOWN = 'down';

/** Ordered best → worst. Exported so consumers/tests can enumerate exhaustively. */
export const HP_GRADES = [
  HP_GRADE_HEALTHY,
  HP_GRADE_WOUNDED,
  HP_GRADE_CRITICAL,
  HP_GRADE_DOWN,
];

/**
 * The ONE threshold ladder (DQ-4). > 50% healthy, > 25% wounded, > 0 critical,
 * ≤ 0 down.
 *
 * `down` is not a fourth colour in the ruling's green→yellow→red scheme — it is
 * a darker RED, preserving the distinction VitalStatsCard already shipped
 * (`current <= 0` → red-600 text / red-700 bar) and extending it to the four
 * surfaces that lacked it. A downed creature now reads differently from a
 * nearly-dead one on every surface.
 *
 * @param {number} current
 * @param {number} max
 * @returns {'healthy'|'wounded'|'critical'|'down'}
 */
export function hpGrade(current, max) {
  const cur = Number(current);
  const m = Number(max);
  // Ungradeable → alarm end. See [RULE 36] in the header.
  if (!Number.isFinite(cur) || !Number.isFinite(m) || m <= 0) return HP_GRADE_CRITICAL;
  if (cur <= 0) return HP_GRADE_DOWN;
  const pct = cur / m;
  if (pct > 0.5) return HP_GRADE_HEALTHY;
  if (pct > 0.25) return HP_GRADE_WOUNDED;
  return HP_GRADE_CRITICAL;
}

/**
 * Per-role Tailwind class strings for each grade. One palette family
 * (emerald / amber / red) across every role — that is what "one universal
 * scheme" means. Roles differ only in weight, because a chip background, a
 * solid bar fill, and body text need different contrast against the same
 * gray-900 shell.
 *
 * `chip` is CombatStatusStrip's shipped tri-class, byte-for-byte for the three
 * graded rows — this refactor must not restyle the surface that already
 * satisfied the ruling.
 */
const GRADE_CLASSES = {
  [HP_GRADE_HEALTHY]: {
    chip: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40',
    bar: 'bg-emerald-500',
    text: 'text-emerald-400',
  },
  [HP_GRADE_WOUNDED]: {
    chip: 'bg-amber-900/40 text-amber-300 border-amber-700/40',
    bar: 'bg-amber-500',
    text: 'text-amber-400',
  },
  [HP_GRADE_CRITICAL]: {
    chip: 'bg-red-900/40 text-red-300 border-red-700/40',
    bar: 'bg-red-500',
    text: 'text-red-400',
  },
  [HP_GRADE_DOWN]: {
    chip: 'bg-red-950/60 text-red-200 border-red-800/60',
    bar: 'bg-red-700',
    text: 'text-red-600',
  },
};

/** Chip treatment (background + text + border) — CombatStatusStrip's HP pill. */
export function hpChipClasses(current, max) {
  return GRADE_CLASSES[hpGrade(current, max)].chip;
}

/** Solid bar-fill background — VitalStatsCard, PartyPanel, CombatTab, MonsterSheet. */
export function hpBarClass(current, max) {
  return GRADE_CLASSES[hpGrade(current, max)].bar;
}

/** Numeric HP text colour — VitalStatsCard's big readout, PartyPanel's label. */
export function hpTextClass(current, max) {
  return GRADE_CLASSES[hpGrade(current, max)].text;
}

/**
 * Raw class map, exported so the contract test can assert the palette is one
 * family per grade without re-declaring the strings (Rule 22 — the test reads
 * production, it does not mirror it).
 */
export { GRADE_CLASSES as HP_GRADE_CLASSES };
